import useAccountQuery from 'api/queries/useAccountQuery';
import { usePlusMembershipLifetimeSavingsQuery } from 'api/queries/usePlusMembershipLifetimeSavingsQuery';
import { usePlusMembershipOptionsQuery } from 'api/queries/usePlusMembershipOptionsQuery';
import {
    AVERAGE_MEMBERSHIP_SAVINGS,
    BRANDING_NAME,
    CARRIER_SHIP_FEE_REDUCTION,
    MEMBERSHIP_COST,
    SHORT_BRANDING_NAME,
    TRIAL_LENGTH_DAYS,
} from 'constants/plusMembership';
import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import useGlobalStore from 'hooks/useGlobalStore';

export function useAccountPlusMembershipHook() {
    const { isMisfitsTheme } = useGlobalStore();
    const brandDomain = isMisfitsTheme ? MISFITS_THEME_DOMAIN : IMPERFECT_THEME_DOMAIN;

    const plusMembershipBranding = BRANDING_NAME[brandDomain];
    const plusMembershipShortBranding = SHORT_BRANDING_NAME[brandDomain];

    const { data: account = {} } = useAccountQuery();
    const { data: plusMembershipOptionsQueryData = {}, isLoading: isPlusMembershipOptionsLoading } =
        usePlusMembershipOptionsQuery();

    const averageMembershipSavingsPeriod = 'year';
    const averageMembershipSavings =
        AVERAGE_MEMBERSHIP_SAVINGS[account?.isDeliveredByImperfect ? 'dbi' : 'cs'][averageMembershipSavingsPeriod];

    const isPlusMembershipAvailable =
        plusMembershipOptionsQueryData?.typeOfMembership === 'PAID' && (account?.plusMemberships ?? []).length === 0;
    const isTrialMembershipAvailable =
        plusMembershipOptionsQueryData?.typeOfMembership === 'FREE_TRIAL' &&
        (account?.plusMemberships ?? []).length === 0;
    const plusMembershipFee = plusMembershipOptionsQueryData?.price ?? MEMBERSHIP_COST;
    const earlyRenewalOffer = plusMembershipOptionsQueryData?.earlyRenewalOffer || {};

    let renewalCost = plusMembershipFee;
    let renewalDiscountCopy = '';

    if (earlyRenewalOffer?.discountType === 'dollar') {
        renewalCost = plusMembershipFee - earlyRenewalOffer.discountAmount;
        renewalDiscountCopy = `$${earlyRenewalOffer.discountAmount}`;
    } else if (earlyRenewalOffer?.discountType === 'percent') {
        renewalCost = plusMembershipFee * (1 - earlyRenewalOffer.discountAmount);
        renewalDiscountCopy = `${(earlyRenewalOffer.discountAmount * 100).toFixed(0)}%`;
    }

    const shippingFees =
        account && account.shippingFee
            ? {
                  plusMembershipShippingFee: account.isDeliveredByImperfect
                      ? 0
                      : account.shippingFee - CARRIER_SHIP_FEE_REDUCTION,
                  plusMembershipShippingSavings: account.isDeliveredByImperfect
                      ? account.shippingFee
                      : CARRIER_SHIP_FEE_REDUCTION,
              }
            : null;

    const plusMemberships = account?.plusMemberships ?? [];
    const customerTrial = plusMemberships.find(
        (plusMembership) => plusMembership.term.typeOfMembership === 'FREE_TRIAL'
    );
    const customerPaidMembership = plusMemberships.find(
        (plusMembership) => plusMembership.term.typeOfMembership === 'PAID'
    );

    const genericMembership = customerPaidMembership ?? customerTrial;

    const sharedMembershipValues = !!genericMembership && {
        memberSinceDate: `${genericMembership?.memberSince?.date ?? genericMembership.term.startDate.date}`,
        startDate: `${genericMembership.term.startDate.date}`,
        endDate: `${genericMembership.term.endDate.date}`,
        isAutoRenew: genericMembership.term.isAutoRenew,
        status: genericMembership.term.status,
        price: genericMembership.variant.price,
        paidDurationDays: genericMembership.variant.paidDurationDays,
        variantId: genericMembership.variant.id,
    };

    const trial =
        customerTrial && !customerPaidMembership
            ? {
                  ...sharedMembershipValues,
                  trialDurationDays: customerTrial.variant.freeTrialDurationDays,
              }
            : null;

    const membership = customerPaidMembership
        ? { ...sharedMembershipValues, isAutoRenewEnabled: customerPaidMembership.term.isAutoRenew }
        : null;

    const plusMembershipEligibleOrder = account?.nextOrder?.plusMemberPriceEligible;

    const isPlusMember = !!membership;
    const freeTrialExpirationDate = trial?.endDate;

    let membershipStatus = 'eligible';

    if (isPlusMember) {
        membershipStatus = 'active';
    } else if (freeTrialExpirationDate) {
        membershipStatus = 'trial';
    }

    const availableCredits = {
        credits: plusMembershipOptionsQueryData?.credits,
        creditsExpirationDays: plusMembershipOptionsQueryData?.creditsExpirationDays,
    };

    const isMemberDealsRoute = window.location.pathname === '/shop/aisle/member-deals';
    const isAccountRoute = window.location.pathname === '/account';
    const isMembershipRoute = window.location.pathname === '/account/membership';
    const isSavingsTrackerExposurePath = isMemberDealsRoute || isAccountRoute || isMembershipRoute;

    const shouldFetchLifetimeSavings = isPlusMember && isSavingsTrackerExposurePath;

    const { data: plusMembershipLifetimeSavingsQueryData = {}, isLoading: isPlusMembershipLifetimeSavingsLoading } =
        usePlusMembershipLifetimeSavingsQuery(shouldFetchLifetimeSavings);

    const memberSavings =
        (plusMembershipLifetimeSavingsQueryData.totalPlusItemSavings || 0) +
        (plusMembershipLifetimeSavingsQueryData.totalPlusShippingSavings || 0);

    const isSavingsTrackerEnabled =
        shouldFetchLifetimeSavings && !isPlusMembershipLifetimeSavingsLoading && memberSavings >= 1;

    return {
        averageMembershipSavings,
        averageMembershipSavingsPeriod,
        plusMembershipBranding,
        plusMembershipShortBranding,
        isPlusMembershipOptionsLoading,
        isSavingsTrackerEnabled,
        memberSavings,
        availableCredits,
        availableTrialMembership: isTrialMembershipAvailable
            ? {
                  trialDurationDays: plusMembershipOptionsQueryData?.membershipDuration ?? TRIAL_LENGTH_DAYS,
                  variantId: plusMembershipOptionsQueryData?.plusMembershipVariantID,
              }
            : null,
        availablePlusMembership: isPlusMembershipAvailable
            ? {
                  membershipDurationDays: plusMembershipOptionsQueryData?.membershipDuration ?? TRIAL_LENGTH_DAYS,
                  variantId: plusMembershipOptionsQueryData?.plusMembershipVariantID,
              }
            : null,
        plusMembershipFee,
        shippingFees,
        trial,
        membership,
        price: {
            annual: genericMembership?.variant?.price ?? plusMembershipOptionsQueryData?.price ?? MEMBERSHIP_COST,
        },
        plusMembershipEligibleOrder,
        membershipStatus,
        membershipRenewalDate: plusMembershipOptionsQueryData?.membershipRenewalDate,
        membershipExpirationDate: sharedMembershipValues?.endDate,
        renewalCost,
        renewalDiscountCopy,
    };
}
